import React, { useState } from 'react'
import TextWrapper from 'components/custom/TextWrapper'
import { Modal, Box, TextField, Tabs, Tab, MenuItem } from '@mui/material'
import PaperWrapper from 'components/custom/PaperWrapper'
import TitleWrapper from 'components/custom/TitleWrapper'
import { eColor, eTypographyVariant } from 'components/types'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { CalculationProcessStepListing } from 'store/types/assembler'

interface ModalYesNo {
  title: string
  message: string
  yesOption: () => void
  open: boolean
  handleClose: () => void
}

export const ModalYesNo: React.FC<ModalYesNo> = ({
  title,
  message,
  yesOption,
  open,
  handleClose,
}) => {
  const { t } = useTranslation()

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className=" absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[40%]">
        <PaperWrapper className="p-4">
          <TitleWrapper
            id="modal-modal-title"
            variant={eTypographyVariant.h6}
            className="2xl:text-xl"
          >
            {t(title)}
          </TitleWrapper>
          <TextWrapper
            id="modal-modal-description"
            className="mb-4"
            sx={{ marginTop: 2 }}
          >
            {t(message)}
          </TextWrapper>
          <div className="flex flex-wrap gap-2" aria-hidden="true">
            <ButtonWrapper
              color={eColor.secondary}
              aria-label={t('general.yes')}
              onClick={(e) => {
                e.stopPropagation() // <--- crucial
                yesOption()
              }}
            >
              {t('general.yes')}
            </ButtonWrapper>
            <ButtonWrapper
              aria-label={t('general.no')}
              onClick={() => handleClose()}
            >
              {t('general.no')}
            </ButtonWrapper>
          </div>
        </PaperWrapper>
      </Box>
    </Modal>
  )
}

interface ModalRename {
  title: string
  message: string
  open: boolean
  handleClose: () => void
  // Our rename callback can receive the new name:
  yesOption?: (newName: string) => void
  // (Optional) prefill the text field with an existing name
  currentName?: string
  existingNames?: string[] // new prop
}

export const ModalRename: React.FC<ModalRename> = ({
  title,
  message,
  yesOption,
  open,
  handleClose,
  currentName,
  existingNames,
}) => {
  const { t } = useTranslation()

  const [newName, setNewName] = useState(currentName || '')
  const [errorMessage, setErrorMessage] = useState('')

  // Reset local fields when the modal opens or currentName changes
  useEffect(() => {
    setNewName(currentName || '')
    setErrorMessage('')
  }, [open, currentName])

  const handleConfirm = () => {
    // Trim the new name just for consistency
    const trimmedName = newName.trim()

    // 2) Check if the name is already in the list (case-insensitive)
    //    ignoring the currently selected assembly’s name if you want
    if (existingNames?.includes(trimmedName.toLowerCase())) {
      setErrorMessage('Entered name already exists.')
      return
    }

    // If no issues, call yesOption
    if (yesOption) {
      yesOption(trimmedName)
    }
    handleClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[40%]">
        <PaperWrapper className="p-4">
          <TitleWrapper
            id="modal-modal-title"
            variant={eTypographyVariant.h6}
            className="2xl:text-xl"
          >
            {t(title)}
          </TitleWrapper>

          <TextWrapper
            id="modal-modal-description"
            className="mb-4"
            sx={{ marginTop: 2 }}
          >
            {t(message)}
          </TextWrapper>

          <TextField
            label={t('Enter new name')}
            variant="outlined"
            fullWidth
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value)
              setErrorMessage('')
            }}
            sx={{ mb: 2 }}
            error={Boolean(errorMessage)}
            helperText={errorMessage || ''}
          />

          <div className="flex flex-wrap gap-2" aria-hidden="true">
            <ButtonWrapper
              color={eColor.secondary}
              aria-label={t('general.rename')}
              onClick={handleConfirm}
            >
              {t('general.rename')}
            </ButtonWrapper>
            <ButtonWrapper
              aria-label={t('general.cancel')}
              onClick={handleClose}
            >
              {t('general.cancel')}
            </ButtonWrapper>
          </div>
        </PaperWrapper>
      </Box>
    </Modal>
  )
}

export interface ModalNewNameProps {
  title: string
  message: string
  open: boolean
  handleClose: () => void
  yesOption: (nameEntered: string) => void
  existingNames: string[] // new prop for validation
}

export const ModalNewName: React.FC<ModalNewNameProps> = ({
  title,
  message,
  open,
  handleClose,
  yesOption,
  existingNames,
}) => {
  const { t } = useTranslation()

  // Local state for the new assembly name
  const [newName, setNewName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // If the modal re-opens, clear the name & errors
  useEffect(() => {
    if (open) {
      setNewName('')
      setErrorMessage('')
    }
  }, [open])

  const handleConfirm = () => {
    const trimmedName = newName.trim()

    // Optional: disallow empty string
    if (!trimmedName) {
      setErrorMessage(
        t('assembler.modal.error.emptyName') || 'Name cannot be empty.'
      )
      return
    }

    // Check if name already exists
    if (existingNames.includes(trimmedName.toLowerCase())) {
      setErrorMessage(
        t('assembler.modal.error.nameExists') || 'Name already exists.'
      )
      return
    }

    // If all good, pass the name up
    yesOption(trimmedName)
    handleClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[40%]">
        <PaperWrapper className="p-4">
          <TitleWrapper
            id="modal-modal-title"
            variant={eTypographyVariant.h6}
            className="2xl:text-xl"
          >
            {t(title)}
          </TitleWrapper>

          <TextWrapper
            id="modal-modal-description"
            className="mb-4"
            sx={{ marginTop: 2 }}
          >
            {t(message)}
          </TextWrapper>

          <TextField
            label={'Assembly Name'}
            variant="outlined"
            fullWidth
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value)
              setErrorMessage('')
            }}
            sx={{ mb: 2 }}
            error={Boolean(errorMessage)}
            helperText={errorMessage || ''}
          />

          <div className="flex flex-wrap gap-2" aria-hidden="true">
            <ButtonWrapper
              color={eColor.secondary}
              aria-label={t('general.create')}
              onClick={handleConfirm}
            >
              {t('general.create')}
            </ButtonWrapper>
            <ButtonWrapper
              aria-label={t('general.cancel')}
              onClick={handleClose}
            >
              {t('general.cancel')}
            </ButtonWrapper>
          </div>
        </PaperWrapper>
      </Box>
    </Modal>
  )
}

interface ModalSaveCalculationProcessStepProps {
  title: string
  message: string
  open: boolean
  handleClose: () => void
  yesOption: (name: string) => void
  existingNames?: string[]
}

export const ModalSaveCalculationProcessStep: React.FC<
  ModalSaveCalculationProcessStepProps
> = ({ title, message, open, handleClose, yesOption, existingNames = [] }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (open) {
      setName('')
      setErrorMessage('')
    }
  }, [open])

  const handleSave = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setErrorMessage(
        t('assembler.modal.error.emptyName') || 'Name is required.'
      )
      return
    }

    if (existingNames.includes(trimmed.toLowerCase())) {
      setErrorMessage(
        t('assembler.modal.error.nameExists') || 'Name already exists.'
      )
      return
    }

    yesOption(trimmed)
    handleClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[40%]">
        <PaperWrapper className="p-4">
          <TitleWrapper>{t(title)}</TitleWrapper>
          <TextWrapper sx={{ mt: 2 }}>{t(message)}</TextWrapper>
          <TextField
            label={t('general.name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErrorMessage('')
            }}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          />
          <div className="flex gap-2">
            <ButtonWrapper
              color={eColor.secondary}
              onClick={handleSave}
              aria-label={t('general.save')}
            >
              {t('general.save')}
            </ButtonWrapper>
            <ButtonWrapper
              onClick={handleClose}
              aria-label={t('general.cancel')}
            >
              {t('general.cancel')}
            </ButtonWrapper>
          </div>
        </PaperWrapper>
      </Box>
    </Modal>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  onCreate: (stepName: string) => void
  onSelectExisting: (step: CalculationProcessStepListing) => void
  existingSteps: CalculationProcessStepListing[]
}

export const ModalAddCalculationStep: React.FC<Props> = ({
  open,
  onClose,
  onCreate,
  onSelectExisting,
  existingSteps,
}) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const [newName, setNewName] = useState('')
  const [selectedStepId, setSelectedStepId] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setTab(0)
      setNewName('')
      setSelectedStepId('')
      setError('')
    }
  }, [open])

  const handleCreate = () => {
    if (!newName.trim()) {
      setError('Please enter a step name.')
      return
    }
    onCreate(newName.trim())
    onClose()
  }

  const handleSelectExisting = () => {
    const selected = existingSteps.find((s) => s.id === selectedStepId)
    if (selected) {
      onSelectExisting(selected)
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90%] md:w-[70%] lg:w-[55%]"
        sx={{ maxHeight: '80vh', overflowY: 'auto' }}
      >
        <PaperWrapper className="p-4">
          <TitleWrapper
            id="modal-modal-title"
            variant={eTypographyVariant.h6}
            className="2xl:text-xl"
          >
            {t('Add Calculation Step')}
          </TitleWrapper>

          <Tabs
            value={tab}
            onChange={(_, val) => setTab(val)}
            className="mt-2 mb-4"
          >
            <Tab label="Create New" />
            <Tab label="Select Existing" />
          </Tabs>

          {tab === 0 && (
            <TextField
              label="New Step Name"
              fullWidth
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value)
                setError('')
              }}
              error={!!error}
              helperText={error}
              className="mb-4"
            />
          )}

          {tab === 1 && (
            <TextField
              select
              label="Choose Existing Step"
              fullWidth
              value={selectedStepId}
              onChange={(e) => setSelectedStepId(e.target.value)}
              className="mb-4"
            >
              {existingSteps.map((step) => (
                <MenuItem key={step.id} value={step.id}>
                  {step.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <ButtonWrapper onClick={onClose}>
              {t('general.cancel')}
            </ButtonWrapper>

            {tab === 0 ? (
              <ButtonWrapper onClick={handleCreate} color={eColor.secondary}>
                {t('general.create')}
              </ButtonWrapper>
            ) : (
              <ButtonWrapper
                onClick={handleSelectExisting}
                color={eColor.secondary}
                disabled={!selectedStepId}
              >
                {t('general.add')}
              </ButtonWrapper>
            )}
          </div>
        </PaperWrapper>
      </Box>
    </Modal>
  )
}
